import React, {
  useCallback,
  useContext,
  useState,
} from 'react';
import format from 'string-template';
import { observer } from 'mobx-react';
import { compose } from 'recompose';

import { ReactComponent as CopyIcon } from '../../../../../../assets/icons/v2/duplicate.svg';
import { SaveButton } from '../../../../../../components/Button/ActionButtons';

import ExternalCoachContext from '../../../../../context/ExternalCoachContext';
import useToast from '../../../../../hooks/useToast';
import { MEAL_PLAN_INTRODUCTION_PLACEHOLDER } from '../../../../../utils/appCustomization';

import {
  PlaceholderButton,
  PlaceholdersContainer,
  PlaceholderTip,
} from '../../../../../components/PlaceHolder/styles';
import ToggleButtonGroup from '../../../../../components/ToggleButtonGroup';
import EditItem from '../../../components/EditItem';
import {
  TitledActionContainer,
  TitledActionContainerFooter,
  TitledActionContainerHeader,
  TitledActionContainerSection,
} from '../../../styles';

import texts from './texts.json';
import { MEAL_PLAN_MESSAGE_MAX_CHARACTERS_COUNT } from './utils';
import {
  InputContainer,
  StyledTextField,
} from './styles';

const IntroType = {
  DEFAULT: 'mealPlanIntroMessage',
  CHALLENGE: 'challengeMealPlanIntroMessage',
};

const introTypeOptions = [
  { value: IntroType.DEFAULT, label: texts.defaultIntro },
  { value: IntroType.CHALLENGE, label: texts.challengeIntro },
];

const MealPlanIntroductionConfig = () => {
  const minRows = 3;
  const maxRows = 18;

  const { coachDoc } = useContext(ExternalCoachContext);

  const {
    mealPlanConfig: {
      mealPlanIntroMessage = '',
      challengeMealPlanIntroMessage = '',
    },
  } = coachDoc;

  const [introType, setIntroType] = useState(IntroType.DEFAULT);
  const [introMessages, setIntroMessages] = useState({
    mealPlanIntroMessage,
    challengeMealPlanIntroMessage,
  });
  const introMessage = introMessages[introType];

  const { showToast } = useToast();

  const handleSave = useCallback(async () => {
    await coachDoc.setMealPlanIntroMessages({
      mealPlanIntroMessage,
      challengeMealPlanIntroMessage,
      [introType]: introMessage,
    });
    showToast(texts.messageSaved);
  }, [
    coachDoc,
    mealPlanIntroMessage,
    challengeMealPlanIntroMessage,
    introType,
    introMessage,
    showToast,
  ]);

  const copyPlaceholderValue = useCallback(async (placeholderValue) => {
    await navigator.clipboard.writeText(`{${placeholderValue}}`);
    showToast(texts.placeholderCopied);
  }, [
    showToast,
  ]);

  const handleTextChange = useCallback((event) => {
    const newValue = event.target.value;
    setIntroMessages((messages) => ({ ...messages, [introType]: newValue }));
  }, [introType]);

  const isError = introMessage.length > MEAL_PLAN_MESSAGE_MAX_CHARACTERS_COUNT;
  const isUnchanged = introMessage === (coachDoc.mealPlanConfig[introType] || '');
  const isChallengeIntro = introType === IntroType.CHALLENGE;

  return (
    <TitledActionContainer>
      <TitledActionContainerHeader>{texts.header}</TitledActionContainerHeader>
      <TitledActionContainerSection>
        <EditItem label={texts.label}>
          <InputContainer>
            <ToggleButtonGroup
              value={introType}
              options={introTypeOptions}
              onChange={setIntroType}
            />
            <StyledTextField
              value={introMessage}
              onChange={handleTextChange}
              placeholder={(isChallengeIntro && mealPlanIntroMessage) || texts.defaultMessage}
              error={isError}
              helperText={isError
                ? format(texts.characterCount, {
                  count: introMessage.length,
                  maxCount: MEAL_PLAN_MESSAGE_MAX_CHARACTERS_COUNT,
                })
                : isChallengeIntro && texts.challengeIntroHint}
              multiline
              minRows={minRows}
              maxRows={maxRows}
            />
          </InputContainer>
        </EditItem>
        <EditItem>
          <PlaceholdersContainer>
            <PlaceholderTip>{texts.placeholderTitle}</PlaceholderTip>
            {Object.values(MEAL_PLAN_INTRODUCTION_PLACEHOLDER).map((placeholderValue) => {
              const displayText = `{${placeholderValue}}`;
              return (
                <PlaceholderButton
                  key={placeholderValue}
                  startIcon={<CopyIcon />}
                  onClick={() => copyPlaceholderValue(placeholderValue)}
                >
                  {displayText}
                </PlaceholderButton>
              );
            })}
            <PlaceholderTip>{texts.placeholderDescription}</PlaceholderTip>
          </PlaceholdersContainer>
        </EditItem>
      </TitledActionContainerSection>
      <TitledActionContainerFooter>
        <SaveButton
          onClick={handleSave}
          disabled={isError || isUnchanged}
        >
          {texts.save}
        </SaveButton>
      </TitledActionContainerFooter>
    </TitledActionContainer>
  );
};

export default compose(
  observer,
)(MealPlanIntroductionConfig);
